import React, { useState } from 'react';
import { Typography, Paper, Accordion, AccordionSummary, AccordionDetails, Divider, Button, Box, Snackbar, CircularProgress } from '@mui/material';
import { Alert } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import TextEditor from './component/TextEditor';
import CustomButtonEditor from './component/CustomButtonEditor';
import MenuButtonEditor from './component/MenuButtonEditor';
import axiosInstance from '../../utils/axios';

const Settings = () => {
  const [restarting, setRestarting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleRestartAllBots = async () => {
    if (!window.confirm('Вы уверены, что хотите перезапустить всех ботов? Это может занять некоторое время.')) {
      return;
    }

    setRestarting(true);
    try {
      const response = await axiosInstance.post('/bots-settings/restart-all');
      setSnackbar({ 
        open: true, 
        message: response.data.message || 'Все боты успешно перезапущены!', 
        severity: 'success' 
      });
    } catch (error) {
      console.error('Error restarting bots:', error);
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || 'Ошибка при перезапуске ботов', 
        severity: 'error' 
      });
    } finally {
      setRestarting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 3, bgcolor: 'background.paper' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#333', mb: 0 }}>
            Настройки
          </Typography>
          <Typography variant="body1" sx={{ color: '#555' }}>
            Здесь вы можете настроить различные параметры приложения.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={restarting ? <CircularProgress size={20} color="inherit" /> : <RestartAltIcon />}
          onClick={handleRestartAllBots}
          disabled={restarting}
          sx={{
            backgroundColor: '#d32f2f',
            color: '#fff',
            fontWeight: 'bold',
            '&:hover': {
              backgroundColor: '#b71c1c',
            },
            '&:disabled': {
              backgroundColor: '#e57373',
            },
          }}
        >
          {restarting ? 'Перезапуск...' : 'Перезапустить всех ботов'}
        </Button>
      </Box>

      {/* Разделитель для лучшего визуального разделения */}
      <Divider sx={{ my: 2 }} />

      {/* Аккордеоны для раскрывающихся блоков */}
      <Accordion sx={{ mb: 2, borderRadius: 1, boxShadow: 1 }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: '#00796b' }} />}
          aria-controls="panel1a-content"
          id="panel1a-header"
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 1,
            '&:hover': { backgroundColor: '#e0f2f1' },
          }}
        >
          <Typography sx={{ fontWeight: 'bold', color: '#00796b' }}>Тексты в ботах</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ backgroundColor: '#f1f8e9' }}>
          <Typography sx={{ color: '#333' }}>
            <TextEditor/>
          </Typography>
        </AccordionDetails>
      </Accordion>

      <Accordion sx={{ mb: 2, borderRadius: 1, boxShadow: 1 }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: '#00796b' }} />}
          aria-controls="panel2a-content"
          id="panel2a-header"
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 1,
            '&:hover': { backgroundColor: '#e0f2f1' },
          }}
        >
          <Typography sx={{ fontWeight: 'bold', color: '#00796b' }}>Кастомная кнопка</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ backgroundColor: '#f1f8e9' }}>
          <CustomButtonEditor />
        </AccordionDetails>
      </Accordion>

      <Accordion sx={{ mb: 2, borderRadius: 1, boxShadow: 1 }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: '#00796b' }} />}
          aria-controls="panel3a-content"
          id="panel3a-header"
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 1,
            '&:hover': { backgroundColor: '#e0f2f1' },
          }}
        >
          <Typography sx={{ fontWeight: 'bold', color: '#00796b' }}>Кнопка меню (Menu Button)</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ backgroundColor: '#f1f8e9' }}>
          <MenuButtonEditor />
        </AccordionDetails>
      </Accordion>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default Settings;
